import { useTranslation } from "react-i18next";

type ValidationError = { error?: string | false; errorInline?: boolean };

type ValidationPatternError = {
  pattern?: string;
  patternError?: string;
};

type ValidationMinLengthError = { minLength?: number; minLengthError?: string };

type ValidationMinError = {
  min?: number;
  minError?: string;
};

type ValidationMaxError = {
  max?: number;
  maxError?: string;
};

type ValidationMaxLengthError = {
  maxLength?: number;
  maxLengthError?: string;
};

type ValidationStepError = {
  step?: number;
  stepError?: string;
};

type ValidationTypeError =
  | { type?: string }
  | {
      type: "email";
      emailError?: string;
    };

type ValidationRequiredError = {
  required?: boolean;
  requiredError?: string;
};

export type TextFieldValidationProps = ValidationError &
  ValidationPatternError &
  ValidationMinError &
  ValidationMaxError &
  ValidationStepError &
  ValidationMinLengthError &
  ValidationMaxLengthError &
  ValidationTypeError &
  ValidationRequiredError;

export type SelectValidationProps = ValidationError & ValidationRequiredError;

export type CheckboxValidationProps = ValidationError & ValidationRequiredError;

type ValidationProps = ValidationError &
  ValidationPatternError &
  ValidationMinError &
  ValidationMaxError &
  ValidationStepError &
  ValidationMinLengthError &
  ValidationMaxLengthError &
  ValidationTypeError &
  ValidationRequiredError;

export default function useValidationError(
  ref: React.RefObject<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >,
  opts: ValidationProps,
): string | null {
  const { t } = useTranslation();

  if (!ref.current?.validity) return null;

  const validity = ref.current.validity;

  if (opts.error) {
    ref.current.setCustomValidity(opts.error);

    return opts.error;
  } else if (ref.current.validationMessage) {
    ref.current.setCustomValidity("");
  }

  if (validity.patternMismatch) {
    if ("patternError" in opts && opts.patternError) return opts.patternError;

    return t("common:error.invalidInput", "Invalid input", {
      pattern: opts.pattern,
    });
  }

  if (validity.rangeUnderflow) {
    if ("minError" in opts && opts.minError) return opts.minError;

    return t(
      "common:error.numUnderflow",
      "Too short! Minimum length is {{count}} characters",
      { min: opts.min },
    );
  }

  if (validity.rangeOverflow) {
    if ("maxError" in opts && opts.maxError) return opts.maxError;

    return t(
      "common:error.numOverflow",
      "Too long! Maximum length is {{count}} characters",
      { max: opts.max },
    );
  }

  if (validity.stepMismatch && "stepError" in opts && opts.stepError) {
    return opts.stepError;
  }

  if (validity.tooShort) {
    if ("minLengthError" in opts && opts.minLengthError)
      return opts.minLengthError;

    return t(
      "common:error.tooShort",
      "Too short! Minimum length is {{minLength}} characters",
      { minLength: opts.minLength },
    );
  }

  if (validity.tooLong) {
    if ("maxLengthError" in opts && opts.maxLengthError)
      return opts.maxLengthError;

    return t(
      "common:error.tooLong",
      "Too long! Maximum length is {{maxLength}} characters",
      { maxLength: opts.maxLength },
    );
  }

  if (validity.typeMismatch) {
    if (opts.type === "email") {
      if ("emailError" in opts && opts.emailError) return opts.emailError;

      return t("common:error.invalidEmail", "Invalid email address");
    }

    return t("common:error.invalidInput", "Invalid input");
  }

  if (validity.valueMissing) {
    if ("requiredError" in opts && opts.requiredError)
      return opts.requiredError;

    return t("common:error.required", "Please fill out this field.");
  }

  return ref.current.validationMessage || null;
}
